/**
 * The audible anchor at tick 0.
 *
 * `SYNC_BEEP` is logged by the server at tick 0 and arrives in the same frame
 * as `ROUND_STARTED`. Craft Together plays the equivalent tone out of the
 * speakers and relies on it bleeding into the microphone, which works with
 * laptop speakers and fails completely with headphones — and a study asks
 * people to wear headphones, because otherwise each recording contains the
 * other player's voice.
 *
 * So the tone is *mixed into the recorded stream* rather than merely played.
 * The microphone and an oscillator both feed a `MediaStreamDestination`, and it
 * is that mixed stream the recorder captures. The tone is also sent to the
 * speakers, because a round starting is worth hearing.
 *
 * The result: both files contain a
 * mark at the log's zero, so aligning audio to the event stream needs no stored
 * offset and no wall-clock arithmetic.
 */

/** Short, mid-range and unmistakable against speech. */
const BEEP_HZ = 880
const BEEP_MS = 180
const BEEP_GAIN = 0.25

export interface BeepBus {
  /** What the recorder should capture: the microphone plus the tone. */
  readonly stream: MediaStream
  /** Sound the anchor, into the recording and out of the speakers. */
  beep(): void
  close(): void
  /**
   * Whether the mixing graph is running. A suspended `AudioContext` — the
   * usual state before a gesture, always the state after a reload until the
   * next click or key, and Safari's state until resumed inside a gesture —
   * passes no samples, so a recorder on `stream` would record nothing. When
   * this is false the recorder takes the bare microphone instead.
   */
  running(): boolean
  /** Try to start the graph. Safe to call any number of times. */
  resume(): Promise<void>
}

type AudioContextCtor = new () => AudioContext

function audioContextCtor(): AudioContextCtor | null {
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/**
 * Returns a bus, or a passthrough if WebAudio is unavailable.
 *
 * The fallback records the bare microphone and beeps nowhere. That loses the
 * anchor but not the recording, which is the right way round: a session with
 * audio and no marker can still be aligned by hand from the wall clocks the log
 * carries, a session with no audio cannot be anything.
 */
export function createBeepBus(microphone: MediaStream): BeepBus {
  const Ctor = audioContextCtor()
  if (!Ctor) {
    return bareMicrophone(microphone)
  }

  let context: AudioContext
  let destination: MediaStreamAudioDestinationNode
  try {
    context = new Ctor()
    destination = context.createMediaStreamDestination()
    context.createMediaStreamSource(microphone).connect(destination)
  } catch (err) {
    console.warn('[audio] could not build the sync-beep bus, recording the bare mic:', err)
    return bareMicrophone(microphone)
  }

  return {
    stream: destination.stream,
    beep() {
      try {
        // A suspended context is the usual state before the first gesture; the
        // mic check has already had one, but resuming costs nothing.
        void context.resume?.()

        const oscillator = context.createOscillator()
        const gain = context.createGain()
        oscillator.frequency.value = BEEP_HZ
        gain.gain.value = BEEP_GAIN

        oscillator.connect(gain)
        // Into the file, and into the room.
        gain.connect(destination)
        gain.connect(context.destination)

        const now = context.currentTime
        oscillator.start(now)
        oscillator.stop(now + BEEP_MS / 1000)
        oscillator.onended = () => {
          try {
            gain.disconnect()
            oscillator.disconnect()
          } catch {
            // Already torn down.
          }
        }
      } catch (err) {
        console.warn('[audio] sync beep failed:', err)
      }
    },
    close() {
      void context.close?.().catch(() => undefined)
    },
    running() {
      return context.state === 'running'
    },
    async resume() {
      try {
        await context.resume?.()
      } catch {
        // Not allowed yet; the next gesture will try again.
      }
    },
  }
}

/** A bus that is only the microphone: no beep, always "running". */
function bareMicrophone(microphone: MediaStream): BeepBus {
  return {
    stream: microphone,
    beep: () => undefined,
    close: () => undefined,
    running: () => false,
    resume: async () => undefined,
  }
}
